import api from "./api";
import { toManualPaymentFormData } from "./paymentService";

// Club renewal flow (Request -> pay -> club + coaches reactivated), backed by
// ClubRenewalController. Distinct resource from renewalService.js's
// individual membership renewal — this one always starts from a club whose
// club_status is Expired, and reactivating it also reactivates every coach
// on that club (students are left unchanged).
//
// Unlike membership renewals, this DOES support real online (CyberExt) card
// payment: ClubRenewalController::formatRenewal() calls
// PaymentGatewayPayload::make() and returns a payment_payload on every
// pending renewal (store/show/myRenewals/index alike), and
// UnifiedPaymentConfirmController::confirm() already routes a
// club_renewal_process payment_reference to
// ClubRenewalController::confirmRenewalByGateway() -> the same
// completeRenewal() manualConfirm uses, just tagged "Card Payment". The
// member picks a method via ClubRenewalPaymentModal.jsx
// (src/components/common/), mirroring IdCardReprintPaymentModal.jsx's
// gateway-popup + polling UX but polling checkClubRenewalPaymentStatus below
// (a dedicated status route, unlike reprints which have none) instead of
// re-fetching the renewal itself. manualConfirm (shroff) still works exactly
// as before and takes the renewal's payment_reference directly.
//
// Two independent callers:
//   - A portal member (coach token) requesting/tracking their own club's
//     renewal via the /member/club-renewals endpoints below. Only the
//     primary coach may call store/feePreview/myRenewals — the backend 403s
//     otherwise. The frontend mirrors that check client-side (via `is_primary`
//     on the active club's entry from GET /api/coach/clubs, see
//     coachClubService.js) to decide whether to show the tab at all (see
//     CoachDashboard.jsx) — GET /api/clubs/{id} carries no field the frontend
//     could compare against, so don't reach for that response here.
//   - A system user with "view_payments"/"submit_manual_payment_receipts"
//     (e.g. Instructor) browsing the club-renewal-payments ledger and
//     confirming a shroff-counter payment on a club's behalf.
//
// A manual-confirm call completes the renewal payment immediately, activates
// the club, and reactivates its coaches (new membership dates). It also
// queues a manual_payment_receipts row for Director/Admin audit-only
// acknowledgement through the existing shared queue in paymentService.js
// (listManualPaymentApprovals/approveManualPaymentApproval/
// rejectManualPaymentApproval) — this file has no approve/reject functions
// of its own, and that step no longer gates the payment either way.
//
// Response-shape note: unlike MembershipRenewalController::formatRenewal,
// ClubRenewalController::formatRenewal already joins in club_name, reg_no,
// requested_by_name/email and the payment_status/process_status description
// strings directly — no need for the membership-renewals page's workaround
// of deriving Paid/Pending from `paid_at` alone; trust `payment_status` here.

// GET /api/member/club-renewals — portal-member only (403 if the token
// doesn't belong to a member, or the member isn't the club's primary coach).
// Returns an array of this club's renewals, newest first — same row shape as
// getClubRenewalPayment below.
export const getMyClubRenewals = () =>
  api.get("/api/member/club-renewals").then((r) => (Array.isArray(r.data) ? r.data : []));

// GET /api/member/club-renewals/fee-preview — portal-member (primary coach)
// only. Returns { club_id, club_code, club_name, club_status_id,
// club_expired, membership_end_date, included_coaches, coach_count,
// additional_coach_count, club_registration_fee, additional_fee,
// payable_amount } without creating a renewal row — used to show the fee
// before the coach commits to "Request Renewal".
export const getClubRenewalFeePreview = () => api.get("/api/member/club-renewals/fee-preview").then((r) => r.data);

// POST /api/member/club-renewals — no body. If a Pending (unpaid) renewal
// already exists for this club, returns the existing one (200) instead of
// erroring — safe to call repeatedly without checking first. Otherwise
// creates a new renewal (201) with new_membership_start_date/end_date
// computed from the club's current membership_end_date, and the fee
// computed from its current coach count. 422s if the club isn't Expired yet,
// or if it has no coaches. Returns { message, renewal }.
export const requestClubRenewal = () => api.post("/api/member/club-renewals").then((r) => r.data);

// GET /api/member/club-renewals/{id}/shroff-voucher — the primary coach's
// own printable pay-at-counter voucher PDF for this renewal's fee.
// responseType "blob" so axios doesn't try to JSON-parse the PDF.
export const getMyClubRenewalShroffVoucherPdf = (renewalId) =>
  api.get(`/api/member/club-renewals/${renewalId}/shroff-voucher`, { responseType: "blob" }).then((r) => r.data);

// GET /api/club-renewal-payments — the full club renewal ledger, gated by
// "view_payments". Optional { search } filter (matches payment_reference,
// club name/reg_no, requester initials/lastname/email — see
// ClubRenewalController::index). Returns a plain array: {renewal_id,
// club_id, club_code, club_name, reg_no, requested_by_member_id,
// requested_by_member_code, requested_by_name, requested_by_email,
// income_id, payment_reference, external_payment_reference, coach_count,
// additional_coach_count, club_registration_fee, additional_fee,
// payable_amount, paid_amount, payment_status_id, payment_status,
// payment_method_id, process_status_id, process_status,
// old_membership_start_date, old_membership_end_date,
// new_membership_start_date, new_membership_end_date, paid_at, created_at,
// updated_at}.
export const listClubRenewalPayments = (params = {}) =>
  api.get("/api/club-renewal-payments", { params }).then((r) => (Array.isArray(r.data) ? r.data : []));

// GET /api/club-renewal-payments/{id} — single row, same shape as one entry
// from listClubRenewalPayments above (gated by "view_payment_details").
export const getClubRenewalPayment = (renewalId) => api.get(`/api/club-renewal-payments/${renewalId}`).then((r) => r.data);

// Looks up a single renewal by the reference number printed on its shroff
// voucher, for the Confirm Payment tab's search-then-confirm flow. Unlike
// renewalService.js's equivalent, this can just reuse listClubRenewalPayments
// above — ClubRenewalController::formatRenewal already includes club_name/
// requested_by_name, so there's no need to fall back to the /api/payments
// ledger for a name. That endpoint does a LIKE search, so results are
// filtered here to an exact payment_reference match. Returns null if no
// exact match is found.
export const searchClubRenewalPayment = async (reference) => {
  const ref = String(reference || "").trim();
  if (!ref) return null;
  const rows = await listClubRenewalPayments({ search: ref });
  return rows.find((r) => String(r.payment_reference || "").trim() === ref) || null;
};

// GET /api/club-renewal-payments/{id}/shroff-voucher — operator/printable
// version of the same voucher as getMyClubRenewalShroffVoucherPdf above,
// keyed by renewal id alone since the caller already has admin access.
// Gated by "view_payments". responseType "blob" so axios doesn't try to
// JSON-parse the PDF.
export const getClubRenewalShroffVoucherPdf = (renewalId) =>
  api.get(`/api/club-renewal-payments/${renewalId}/shroff-voucher`, { responseType: "blob" }).then((r) => r.data);

// POST /api/club-renewal-payments/manual-confirm — multipart. payload:
// { payment_reference (required — from the club's own renewal row),
// paid_amount, paid_date, receipt_reference, receipt (File, required), note? }. Gated by
// "submit_manual_payment_receipts". Marks the renewal Paid, activates the
// club, and reactivates its coaches (new membership dates) immediately
// (HTTP 200, not a queued 202) — a manual_payment_receipts row is still
// queued for Director/Admin acknowledgement on the Payments -> Manual
// Approvals tab, but that step is audit-only now and doesn't gate the
// payment. Response: { message, renewal, club: {id, club_code, club_name,
// membership_start_date, membership_end_date, club_status: "Active",
// no_of_coaches}, payment_method: "Shroff Payment", receipt_reference,
// approval_id, approval_status: "Pending" }.
export const confirmClubRenewalManualPayment = (payload) =>
  api.post("/api/club-renewal-payments/manual-confirm", toManualPaymentFormData(payload)).then((r) => r.data);

// POST /api/club-renewal-payments/status — { renewal_id, payment_reference }
// (both required). Unauthenticated route (same as booking-payments/status,
// registration-payments/status) polled while the CyberExt popup is open, to
// detect the moment the gateway's server-to-server callback
// (POST /api/club-renewal-payments/confirm) has flipped this renewal to
// Paid. Returns the same shape as one row from getMyClubRenewals — but like
// store/show/myRenewals (unlike the admin index() listing), this route never
// joins the payment_status category description, so `payment_status` comes
// back null either way; check `paid_at` instead (set the moment
// completeRenewal() runs).
export const checkClubRenewalPaymentStatus = ({ renewal_id, payment_reference }) =>
  api.post("/api/club-renewal-payments/status", { renewal_id, payment_reference }).then((r) => r.data);
